import { useEffect, useRef, useState } from 'react';
import type { EmployeeDirectorySnapshot } from '../../features/people/public';

const cacheLifetime = 60_000;

/** 호출 컴포넌트의 세션 문맥 안에서만 보관하며 닫힌 동안 도착한 응답은 UI를 갱신하지 않습니다. */
export function useSearchDirectory(open: boolean, enabled: boolean, load: () => Promise<EmployeeDirectorySnapshot>) {
  const loader = useRef(load);
  loader.current = load;
  const cache = useRef<{
    data?: EmployeeDirectorySnapshot;
    expiresAt: number;
    pending?: Promise<EmployeeDirectorySnapshot>;
  }>({ expiresAt: 0 });
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<{
    data?: EmployeeDirectorySnapshot;
    loading: boolean;
    error: boolean;
  }>({ loading: false, error: false });

  useEffect(() => {
    if (!open || !enabled) return;
    const current = cache.current;
    if (current.data && current.expiresAt > Date.now()) {
      setState({ data: current.data, loading: false, error: false });
      return;
    }
    let active = true;
    setState({ loading: true, error: false });
    if (!current.pending) {
      current.pending = Promise.resolve().then(() => loader.current()).then((data) => {
        current.data = data;
        current.expiresAt = Date.now() + cacheLifetime;
        return data;
      }).finally(() => { current.pending = undefined; });
    }
    void current.pending.then(
      (data) => { if (active) setState({ data, loading: false, error: false }); },
      () => { if (active) setState({ loading: false, error: true }); },
    );
    return () => { active = false; };
  }, [open, enabled, revision]);

  return {
    ...state,
    retry: () => {
      cache.current.data = undefined;
      cache.current.expiresAt = 0;
      setRevision((value) => value + 1);
    },
  };
}
